import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { PatientNamePipe } from '@modules/patient/patient-name.pipe';
import {
  SequencePickerDialogComponent,
  SequencePickerOutput,
} from '@modules/sequence/sequence-picker/sequence-picker-dialog/sequence-picker-dialog.component';
import { Store } from '@ngrx/store';
import { ChannelType } from '@pixacare/chat';
import { CreatorType, Patient } from '@pixacare/domain';
import { FlattenedTelemonitoring, Telemonitoring } from '@pixacare/dtos';
import { selectClientCode } from '@shared/store/router/router.selectors';
import { selectTelemonitoring } from '@shared/store/telemonitorings/telemonitorings.selectors';
import { first, switchMap } from 'rxjs';
import { AuthorizationsService } from 'src/app/services/authorizations.service';
import { DialogService } from 'src/app/services/dialog.service';
import { DocumentService } from 'src/app/services/document.service';
import { TelemonitoringHttpService } from 'src/app/services/http/telemonitoring.http.service';
import { PatientTab } from 'src/app/shared/models/enums/patient-tabs.enum';
import { Status } from 'src/app/shared/models/enums/status.enum';
import { ActionsService } from 'src/app/shared/models/helpers/actions-service';
import { MenuAction } from 'src/app/shared/models/menu-actions/menu-action';
import { MenuActionId } from 'src/app/shared/models/menu-actions/menu-action-id.enum';
import { telemonitoringsActions } from 'src/app/shared/store/telemonitorings/telemonitorings.actions';
import { TelemonitoringCollaboratorsDialogComponent } from './telemonitoring-collaborators-dialog/telemonitoring-collaborators-dialog.component';
import { TelemonitoringReportComponent } from './telemonitoring-report/telemonitoring-report.component';
import { TelemonitoringSendTutorialComponent } from './telemonitoring-send-tutorial/telemonitoring-send-tutorial.component';

@Injectable({
  providedIn: 'root',
})
export class TelemonitoringActionsService extends ActionsService {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly telemonitoringService = inject(TelemonitoringHttpService);
  private readonly documentService = inject(DocumentService);
  private readonly authorizationsService = inject(AuthorizationsService);
  private readonly dialogService = inject(DialogService);
  private readonly translocoService = inject(TranslocoService);
  private readonly patientNamePipe = inject(PatientNamePipe);

  actions: MenuAction<FlattenedTelemonitoring>[] = [
    {
      id: MenuActionId.TELEMONITORING_TUTORIAL,
      label: this.translocoService.translate('Tutoriel'),
      icon: '@tui.file',
      condition: ({ telemonitoring, patient }: FlattenedTelemonitoring): boolean =>
        this.isTelemonitoringActive(telemonitoring) && this.isTelemonitoringPatientActive(patient),
      execute: ({ telemonitoring }: FlattenedTelemonitoring): void => {
        this.documentService.downloadDocument({
          name: telemonitoring.name || this.translocoService.translate('Tutoriel de télésuivi'),
          document$: this.telemonitoringService.getTutorial(+telemonitoring.id),
        });
      },
    },
    {
      id: MenuActionId.TELEMONITORING_SEND_TUTORIAL,
      label: this.translocoService.translate('Envoyer Tutoriel'),
      icon: '@tui.mail',
      condition: ({ telemonitoring, patient }: FlattenedTelemonitoring): boolean =>
        this.isTelemonitoringActive(telemonitoring) &&
        this.isTelemonitoringPatientActive(patient) &&
        this.isUserOwner(telemonitoring),
      execute: ({ telemonitoring }: FlattenedTelemonitoring): void => {
        this.dialogService
          .openComponent(TelemonitoringSendTutorialComponent, {
            label: this.translocoService.translate('Envoyer le tutoriel'),
            data: {
              telemonitoring,
            },
          })
          .subscribe();
      },
    },
    {
      id: MenuActionId.TELEMONITORING_REPORT,
      label: this.translocoService.translate('Rapport'),
      icon: '@tui.file-text',
      condition: ({ telemonitoring, patient }: FlattenedTelemonitoring): boolean =>
        this.isTelemonitoringActive(telemonitoring) && this.isTelemonitoringPatientActive(patient),
      execute: ({ telemonitoring, patient }: FlattenedTelemonitoring): void => {
        const title = this.translocoService.translate('Rapport de télésuivi de {{name}}', {
          name: this.patientNamePipe.transform(patient),
        });
        this.store
          .select(selectClientCode)
          .pipe(
            first(),
            switchMap((clientCode) =>
              this.dialogService.openComponent(TelemonitoringReportComponent, {
                appearance: 'page',
                data: {
                  clientCode,
                  telemonitoring,
                  title,
                },
              }),
            ),
          )
          .subscribe();
      },
    },
    {
      id: MenuActionId.TELEMONITORING_OPEN_CHAT,
      label: this.translocoService.translate('Ouvrir la conversation'),
      icon: '@tui.message-square',
      condition: ({ telemonitoring }: FlattenedTelemonitoring): boolean => telemonitoring.channelId !== null,
      execute: ({ telemonitoring }: FlattenedTelemonitoring): void => {
        this.router.navigate(['/chat', telemonitoring.channelId], {
          queryParams: { type: ChannelType.TELEMONITORING },
          queryParamsHandling: 'merge',
        });
      },
    },
    {
      id: MenuActionId.TELEMONITORING_SEND_REMINDER,
      label: this.translocoService.translate('Envoyer une relance'),
      icon: '@tui.refresh-ccw',
      condition: ({ telemonitoring, patient }: FlattenedTelemonitoring): boolean =>
        telemonitoring.creatorType === CreatorType.BY_PATIENT &&
        this.isTelemonitoringActive(telemonitoring) &&
        this.isTelemonitoringPatientActive(patient),
      execute: ({ telemonitoring }: FlattenedTelemonitoring): void => {
        this.store.dispatch(
          telemonitoringsActions.sendDepositReminder({
            telemonitoringId: telemonitoring.id,
          }),
        );
      },
    },
    {
      id: MenuActionId.TELEMONITORING_LINK_SEQUENCES,
      label: this.translocoService.translate('Lier des séquences'),
      icon: '@tui.link',
      condition: ({ telemonitoring, patient }: FlattenedTelemonitoring): boolean =>
        this.isTelemonitoringActive(telemonitoring) && this.isTelemonitoringPatientActive(patient),
      execute: ({ telemonitoring }: FlattenedTelemonitoring): void => {
        this.store.dispatch(
          telemonitoringsActions.getSequences({
            tmId: telemonitoring.id,
            clientCode: telemonitoring.clientCode,
          }),
        );

        this.store
          .select(selectTelemonitoring(telemonitoring.id))
          .pipe(first((tm) => !!tm.sequences))
          .subscribe((tm) => {
            const preselectedSequences = tm.sequences;
            const selectedSequenceMessage = this.translocoService.translate(
              "Cette séquence est déjà attribuée à un télésuivi. Si vous la réattribuez, elle sera retirée de l'autre télésuivi.",
            );
            this.dialogService
              .openComponentWithCloseConfirmation<SequencePickerDialogComponent, SequencePickerOutput>(
                SequencePickerDialogComponent,
                {
                  data: {
                    patientId: telemonitoring.patientId,
                    clientCode: telemonitoring.clientCode,
                    departmentIds: telemonitoring.departmentIds,
                    preselectedSequences,
                    disabledSequenceIds: preselectedSequences.map((s) => s.sequenceInstance.id),
                    badgeContent: (sequence) => {
                      const tmId = sequence.sequenceInstance.telemonitoringId;
                      if (tmId && tmId !== telemonitoring.id) {
                        return selectedSequenceMessage;
                      }
                      return null;
                    },
                  },
                  label: this.translocoService.translate('Sélectionnez les séquences à lier au télésuivi'),
                  size: 'l',
                },
              )
              .subscribe((result) => {
                if (result) {
                  const preselectedIds = preselectedSequences.map((s) => s.sequenceInstance.id);
                  const filteredSequenceIds = result.selectedSequenceIds?.filter((id) => !preselectedIds.includes(id));
                  if (filteredSequenceIds.length > 0) {
                    this.store.dispatch(
                      telemonitoringsActions.linkSequences({
                        telemonitoringId: +telemonitoring.id,
                        clientCode: telemonitoring.clientCode,
                        sequenceIds: filteredSequenceIds,
                      }),
                    );
                  }
                }
              });
          });
      },
    },
    {
      id: MenuActionId.TELEMONITORING_ADD_USER,
      label: this.translocoService.translate('Ajouter des collaborateurs'),
      icon: '@tui.user-plus',
      condition: ({ telemonitoring, patient }: FlattenedTelemonitoring): boolean =>
        this.isTelemonitoringActive(telemonitoring) &&
        this.isTelemonitoringPatientActive(patient) &&
        this.isUserOwner(telemonitoring),
      execute: ({ telemonitoring }: FlattenedTelemonitoring): void => {
        this.dialogService
          .openComponentWithCloseConfirmation<TelemonitoringCollaboratorsDialogComponent, number[]>(
            TelemonitoringCollaboratorsDialogComponent,
            {
              label: this.translocoService.translate('Sélectionner les utilisateurs à ajouter'),
              data: {
                telemonitoring,
              },
              size: 'l',
            },
          )
          .subscribe((userIds) => {
            if (userIds?.length > 0) {
              this.store.dispatch(
                telemonitoringsActions.addTelemonitoringUsers({
                  telemonitoringId: telemonitoring.id,
                  userIds,
                }),
              );
            }
          });
      },
    },
    {
      id: MenuActionId.TELEMONITORING_ARCHIVE,
      label: this.translocoService.translate('Archiver'),
      icon: '@tui.archive',
      condition: ({ telemonitoring, patient }: FlattenedTelemonitoring): boolean =>
        this.isTelemonitoringActive(telemonitoring) &&
        this.isTelemonitoringPatientActive(patient) &&
        this.isUserOwner(telemonitoring),
      execute: ({ telemonitoring }: FlattenedTelemonitoring): void => {
        this.store.dispatch(
          telemonitoringsActions.archiveTelemonitoring({
            telemonitoringId: telemonitoring.id,
          }),
        );
      },
    },
    {
      id: MenuActionId.TELEMONITORING_UNARCHIVE,
      label: this.translocoService.translate('Désarchiver'),
      icon: '@tui.archive',
      condition: ({ telemonitoring, patient }: FlattenedTelemonitoring): boolean =>
        this.isTelemonitoringArchived(telemonitoring) &&
        this.isTelemonitoringPatientActive(patient) &&
        this.isUserOwner(telemonitoring),
      execute: ({ telemonitoring }: FlattenedTelemonitoring): void => {
        this.store.dispatch(
          telemonitoringsActions.unarchiveTelemonitoring({
            telemonitoringId: telemonitoring.id,
          }),
        );
      },
    },
    {
      id: MenuActionId.TELEMONITORING_DELETE,
      label: this.translocoService.translate('Supprimer'),
      icon: '@tui.trash',
      status: Status.ERROR,
      condition: ({ telemonitoring, patient }: FlattenedTelemonitoring): boolean =>
        this.isTelemonitoringArchived(telemonitoring) &&
        this.isTelemonitoringPatientActive(patient) &&
        this.isUserOwner(telemonitoring),
      execute: ({ telemonitoring }: FlattenedTelemonitoring): void => {
        this.dialogService
          .openConfirm(this.translocoService.translate('Êtes-vous certain de vouloir supprimer ce télésuivi ?'), {
            content: this.translocoService.translate('Cette action est irréversible.'),
            yes: this.translocoService.translate('Supprimer'),
            isDestructive: true,
          })
          .subscribe((confirm) => {
            if (confirm) {
              this.store.dispatch(
                telemonitoringsActions.deleteTelemonitoring({
                  telemonitoringId: +telemonitoring.id,
                }),
              );
              this.router.navigate(['/client', telemonitoring.clientCode, 'patients', telemonitoring.patientId], {
                queryParamsHandling: 'merge',
                queryParams: { tab: PatientTab.TELEMONITORINGS },
              });
            }
          });
      },
    },
  ];

  private isUserOwner(telemonitoring: Telemonitoring): boolean {
    return this.authorizationsService.userId === telemonitoring.createdBy;
  }

  private isTelemonitoringActive(telemonitoring: Telemonitoring): boolean {
    return telemonitoring && !telemonitoring.isArchived;
  }

  private isTelemonitoringArchived(telemonitoring: Telemonitoring): boolean {
    return telemonitoring && telemonitoring.isArchived;
  }

  private isTelemonitoringPatientActive(patient: Patient): boolean {
    return patient && !patient.isArchived;
  }
}
